import { Modal, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import TextWinds from '../../../common/TextWinds';
import {
  cancelStudentTransferRequest,
  StudentTransferRequestStatus,
  StudentTransferResult,
} from '../../../../api/utils/lms/student/api';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ActionButton from '../../ActionButton';

/**
 * formatToTeacherInfo 로 만든 문자열을 다시 이름/아이디로 나눈다.
 * 이 형식이 생기기 전의 신청이나 관리자가 직접 적은 값은 형식이 다를 수 있어,
 * 형식이 맞지 않으면 null 을 돌려주고 원문을 그대로 보여주게 한다.
 */
const parseToTeacherInfo = (info: string | null) => {
  const matched = info?.match(/^이름:\s*(.+?)\s*\/\s*아이디:\s*(\S+)$/);
  if (!matched) return null;

  return { name: matched[1], uid: matched[2] };
};

/** 신청 상태 표시 이름 */
export const STUDENT_TRANSFER_STATUS_LABEL: Record<
  StudentTransferRequestStatus,
  string
> = {
  PENDING: '처리 대기',
  APPROVED: '이관 승인',
  REJECTED: '이관 거절',
  CANCELED: '신청 취소',
};

/** 한 페이지에 보여줄 신청 건수 */
const PAGE_SIZE = 5;

const STATUS_COLOR: Record<StudentTransferRequestStatus, string> = {
  PENDING: 'blue',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELED: 'default',
};

/** 표 안에서는 길게 쓴 내용을 줄여 보여주고, 전체 내용은 툴팁으로 확인한다. */
const LongText = ({ text }: { text: string | null }) => {
  if (!text) return <span className="text-gray5">-</span>;

  return (
    <Tooltip title={<span className="whitespace-pre-wrap">{text}</span>}>
      <span className="line-clamp-2 whitespace-pre-wrap break-words text-left">
        {text}
      </span>
    </Tooltip>
  );
};

interface StudentTransferRequestListDialogProps {
  open: boolean;
  onClose: () => void;
  requests: StudentTransferResult[];
  isLoading: boolean;
  courseName: string;
  /** 취소 후 목록을 다시 불러온다. */
  reloadTransferRequests: () => void;
}

/**
 * LMS > 학생 관리 > 이관 신청 내역
 *
 * 내가 신청한 학생 이관 건의 처리 상태를 확인하고,
 * 아직 처리되지 않은(PENDING) 신청은 취소할 수 있는 팝업.
 */
const StudentTransferRequestListDialog = ({
  open,
  onClose,
  requests,
  isLoading,
  courseName,
  reloadTransferRequests,
}: StudentTransferRequestListDialogProps) => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);

  const lastPage = Math.max(1, Math.ceil(requests.length / PAGE_SIZE));

  // 팝업을 다시 열면 항상 최신 신청부터 보이도록 첫 페이지로 되돌린다.
  useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  // 신청 취소로 목록이 줄어들면 보고 있던 페이지가 사라질 수 있어 마지막 페이지로 당긴다.
  useEffect(() => {
    setPage((prev) => Math.min(prev, lastPage));
  }, [lastPage]);

  const cancelHandler = (request: StudentTransferResult) => {
    dispatch(
      openConfirm({
        message: `${request.studentName} 학생의 이관 신청을 취소하시겠습니까?`,
        onConfirm: () => {
          cancelStudentTransferRequest(request.id)
            .then(() => {
              reloadTransferRequests();
            })
            .catch((error) => {
              dispatch(openAlert({ message: getErrorMessages(error) }));
            });
        },
      }),
    );
  };

  // 신청 내용(누가·어디로·왜·언제) → 처리 결과(상태·일시·코멘트) → 관리 순서로 읽히게 둔다.
  const columns: ColumnsType<StudentTransferResult> = [
    {
      title: 'No.',
      key: 'index',
      // 최신 신청이 위에 오므로, 가장 오래된 건이 1번이 되도록 역순으로 매긴다.
      // index 는 현재 페이지 안에서의 순번이라 앞 페이지 건수를 더해 전체 순번으로 바꾼다.
      render: (_, __, index) =>
        requests.length - ((page - 1) * PAGE_SIZE + index),
      align: 'center',
      width: 56,
    },
    {
      title: '학생',
      key: 'student',
      render: (_, row) => (
        <div>
          <div>{row.studentName}</div>
          <div>{row.studentUid}</div>
        </div>
      ),
      align: 'center',
    },
    {
      title: '이관 받을 선생님',
      key: 'toTeacherInfo',
      render: (_, row) => {
        const teacher = parseToTeacherInfo(row.toTeacherInfo);

        // 형식을 알아볼 수 없으면 학생 정보처럼 나눌 수 없어 원문 그대로 보여준다.
        if (!teacher) return <LongText text={row.toTeacherInfo} />;

        return (
          <div>
            <div>{teacher.name}</div>
            <div>{teacher.uid}</div>
          </div>
        );
      },
      align: 'center',
    },
    {
      title: '신청 사유',
      key: 'reason',
      render: (_, row) => <LongText text={row.reason} />,
      width: 200,
    },
    {
      title: '신청일시',
      key: 'createdAt',
      render: (_, row) => dayjs(row.createdAt).format('YYYY-MM-DD HH:mm'),
      align: 'center',
    },
    {
      title: '상태',
      key: 'status',
      render: (_, row) => (
        <Tag color={STATUS_COLOR[row.status]} className="m-0" bordered={false}>
          {STUDENT_TRANSFER_STATUS_LABEL[row.status] ?? row.status}
        </Tag>
      ),
      align: 'center',
    },
    {
      title: '처리일시',
      key: 'reviewedAt',
      render: (_, row) =>
        row.reviewedAt ? (
          dayjs(row.reviewedAt).format('YYYY-MM-DD HH:mm')
        ) : (
          <span className="text-gray5">-</span>
        ),
      align: 'center',
    },
    {
      title: '관리자 코멘트',
      key: 'reviewComment',
      render: (_, row) => <LongText text={row.reviewComment} />,
      width: 200,
    },
    {
      title: '관리',
      key: 'cancel',
      render: (_, row) =>
        row.status === 'PENDING' ? (
          <ActionButton
            label="신청 취소"
            icon={XMarkIcon}
            color="orange"
            onClick={() => cancelHandler(row)}
          />
        ) : (
          <span className="text-gray5">-</span>
        ),
      align: 'center',
      width: 110,
    },
  ];

  return (
    <Modal
      title="이관 신청 내역"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      zIndex={9998}
    >
      <div className="flex flex-col gap-3">
        <TextWinds type="content_body3" color="gray7">
          {`나의 ${courseName}에서 신청한 학생 이관 내역입니다. 아직 처리되지 않은(처리 대기) 신청만 취소할 수 있어요.`}
        </TextWinds>

        <Table
          columns={columns}
          dataSource={requests}
          rowKey={(record) => record.id}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: requests.length,
            onChange: setPage,
            hideOnSinglePage: true,
            showSizeChanger: false,
          }}
          size="small"
          loading={isLoading}
          locale={{ emptyText: '이관 신청 내역이 없습니다.' }}
        />
      </div>
    </Modal>
  );
};

export default StudentTransferRequestListDialog;
